import { useState, useEffect } from 'react';

interface InteractiveQuizProps {
    scrollToForm: () => void;
    faqData: Array<{
        question: string;
        answer: string;
    }>;
}

const InteractiveQuiz: React.FC<InteractiveQuizProps> = ({ faqData }) => {
    // Inicializar com valor do localStorage ou 1 como fallback
    const [currentStep, setCurrentStep] = useState(() => {
        const savedStep = localStorage.getItem('teraquiz-current-step');
        if (savedStep) {
            const stepNumber = parseInt(savedStep);
            if (stepNumber >= 1 && stepNumber <= 5) {
                console.log('Inicializando com etapa salva:', stepNumber); // Debug
                return stepNumber;
            }
        }
        console.log('Inicializando com etapa padrão: 1'); // Debug
        return 1;
    });

    // Salvar etapa no localStorage sempre que mudar
    useEffect(() => {
        console.log('Salvando etapa:', currentStep); // Debug
        localStorage.setItem('teraquiz-current-step', currentStep.toString());
    }, [currentStep]);

    const steps = [
        {
            id: 1,
            title: "O que oferecemos",
            content: (
                <div>
                    {/* Hero Section Content */}
                    <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
                        <div className="space-y-6 sm:space-y-8 order-1 lg:order-1">
                            <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-white/20" style={{backgroundColor: 'rgba(193, 255, 114, 0.1)'}}>
                                <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full mr-2 sm:mr-3 animate-pulse" style={{backgroundColor: '#c1ff72'}}></span>
                                <span className="font-bold text-xs sm:text-sm tracking-wider" style={{color: '#c1ff72'}}>BANCO COMPLETO</span>
                            </div>
                            
                            <div>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 leading-tight" style={{color: '#c1ff72'}}>
                                    600 questões + 600 Flashcards + Crie seus Flashcards (ilimitado!)
                                </h2>
                                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                                    O maior banco de questões e de flashcards para você chegar preparado e garantir o seu título de especialista!
                                </h3>
                                <div className="w-16 sm:w-24 h-1 rounded-full mb-4 sm:mb-6" style={{backgroundColor: '#38b6ff'}}></div>
                            </div>
                            
                            <div className="space-y-4 sm:space-y-6">
                                <p className='text-gray-300 text-base sm:text-lg leading-relaxed'>
                                    Sabemos que sua rotina é intensa e que cada segundo conta. Além disso, o volume de conteúdo para estudar para a prova de título é extenso e muitas vezes não dá tempo de revisar todos os conteúdos.
                                </p>
                                <p className='text-gray-300 text-base sm:text-lg leading-relaxed'>
                                    Elaboramos um <strong className='font-bold' style={{color: '#c1ff72'}}>banco de questões completo com os temas que mais caem na prova de título</strong> que você pode realizar no seu dia a dia, com uma didática única!
                                </p>
                            </div>

                            {/* Imagem posicionada após o texto apenas no mobile */}
                            <div className="flex justify-center my-8 lg:hidden">
                                <div className="relative w-full max-w-md sm:max-w-lg">
                                    <img 
                                        src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1755871435099657389_68a878cb.webp" 
                                        alt="Banco de questões Teraquiz" 
                                        className="w-full h-auto rounded-2xl shadow-xl"
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                <div className="flex items-center space-x-3 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#c1ff72'}}>
                                        <span className="text-[12px] sm:text-[16px] font-bold" style={{color: '#0b0b36'}}>600</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-xs sm:text-sm">Questões</h4>
                                        <p className="text-gray-300 text-xs">Comentadas</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center space-x-3 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#38b6ff'}}>
                                        <span className="text-[12px] sm:text-[16px] font-bold text-white">600</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-xs sm:text-sm">Flashcards</h4>
                                        <p className="text-gray-300 text-xs">Exclusivos</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center space-x-3 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#c1ff72'}}>
                                        <span className="text-base sm:text-lg" style={{color: '#0b0b36'}}>∞</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-xs sm:text-sm">Criação</h4>
                                        <p className="text-gray-300 text-xs">Ilimitada</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Imagem lateral no desktop */}
                        <div className="hidden lg:flex justify-center order-2 lg:order-2">
                            <div className="relative w-full max-w-md sm:max-w-lg lg:max-w-none">
                                <img 
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1755871435099657389_68a878cb.webp" 
                                    alt="Banco de questões Teraquiz" 
                                    className="w-full h-auto rounded-2xl shadow-xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            title: "Como funciona",
            content: (
                <div>
                    {/* Diferenciais Section */}
                    <div className="text-center mb-12 sm:mb-16">
                        <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-white/20" style={{backgroundColor: 'rgba(193, 255, 114, 0.1)'}}>
                            <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full mr-2 sm:mr-3 animate-pulse" style={{backgroundColor: '#c1ff72'}}></span>
                            <span className="font-bold text-xs sm:text-sm tracking-wider" style={{color: '#c1ff72'}}>NOSSOS DIFERENCIAIS</span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">Por que escolher nosso banco?</h2>
                        <div className="w-16 sm:w-24 h-1 mx-auto rounded-full" style={{backgroundColor: '#38b6ff'}}></div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        <div className="group relative">
                            <div className="absolute -inset-2 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-all duration-500" style={{backgroundColor: '#38b6ff'}}></div>
                            <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 text-center h-full">
                                <div className="relative mb-4 sm:mb-6">
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#38b6ff'}}>
                                        <svg className="w-8 sm:w-10 h-8 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-white font-bold mb-3 sm:mb-4 text-lg sm:text-xl">600 Questões + 600 Flashcards</h3>
                                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                                    Baseadas no método <strong className='font-bold' style={{color: '#c1ff72'}}>QBank</strong>, modelo consagrado internacionalmente em exames como <strong className='font-bold' style={{color: '#c1ff72'}}>USMLE</strong>, garantindo estudo ativo e direcionado.
                                </p>
                            </div>
                        </div>
                        
                        <div className="group relative">
                            <div className="absolute -inset-2 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-all duration-500" style={{backgroundColor: '#c1ff72'}}></div>
                            <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 text-center h-full">
                                <div className="relative mb-4 sm:mb-6">
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#c1ff72'}}>
                                        <svg className="w-8 sm:w-10 h-8 sm:h-10" style={{color: '#0b0b36'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-white font-bold mb-3 sm:mb-4 text-lg sm:text-xl">Comentários Completos</h3>
                                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                                    Comentários completos: cada questão traz análise das alternativas corretas e incorretas, seguida de uma revisão aprofundada do conteúdo relacionado, conforme os <strong className='font-bold' style={{color: '#c1ff72'}}>Guidelines ASCO, ESMO e atualizações mais recentes da área.</strong>
                                </p>
                            </div>
                        </div>
                        
                        <div className="group relative lg:col-span-1 md:col-span-2 lg:col-span-1">
                            <div className="absolute -inset-2 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-all duration-500" style={{backgroundColor: '#38b6ff'}}></div>
                            <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 text-center h-full">
                                <div className="relative mb-4 sm:mb-6">
                                    <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: '#38b6ff'}}>
                                        <svg className="w-8 sm:w-10 h-8 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-white font-bold mb-3 sm:mb-4 text-lg sm:text-xl">Explicações Detalhadas</h3>
                                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                                    Foco no <strong className='font-bold' style={{color: '#c1ff72'}}>raciocínio clínico</strong> e no <strong className='font-bold' style={{color: '#c1ff72'}}>refinamento técnico</strong> exigidos para o desempenho de alto nível nas provas. Linguagem clara, com organização que permite <strong className='font-bold' style={{color: '#c1ff72'}}>revisão rápida e retenção eficiente.</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 3,
            title: "Recursos práticos",
            content: (
                <div>
                    {/* Estudo Inteligente Section */}
                    <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
                        <div className="space-y-6 sm:space-y-8 order-2 lg:order-1">
                            <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-white/20" style={{backgroundColor: 'rgba(56, 182, 255, 0.1)'}}>
                                <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full mr-2 sm:mr-3 animate-pulse" style={{backgroundColor: '#38b6ff'}}></span>
                                <span className="font-bold text-xs sm:text-sm tracking-wider" style={{color: '#38b6ff'}}>ESTUDO INTELIGENTE</span>
                            </div>
                            
                            <div>
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                                    Acompanhar todos os conteúdos em Oncologia não é fácil, ainda mais na nossa rotina intensa.
                                </h2>
                                <p className="text-gray-300 mb-6 sm:mb-8 text-base sm:text-lg leading-relaxed">
                                    Pensando nisso, elaboramos <strong className='font-bold' style={{color: '#c1ff72'}}>600 questões abordando os conteúdos que caem na prova e no estilo dela.</strong> Além disso, disponibilizamos <strong className='font-bold' style={{color: '#c1ff72'}}>600 flashcards</strong> que você não pode deixar de responder antes de ir para sua prova! Você pode também criar os seus próprios Flashcards para revisão prática.
                                </p>
                            </div>
                            
                            <div className="space-y-3 sm:space-y-4">
                                <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#c1ff72'}}>
                                        <span className="text-base sm:text-[20px] font-bold" style={{color: '#0b0b36'}}>600</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-base sm:text-lg">Questões Comentadas</h4>
                                        <p className="text-gray-300 text-xs sm:text-sm">No estilo da prova com explicações detalhadas</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#38b6ff'}}>
                                        <span className="text-base sm:text-[20px] font-bold text-white">600</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-base sm:text-lg">Flashcards Exclusivos</h4>
                                        <p className="text-gray-300 text-xs sm:text-sm">Para fixação e revisão rápida do conteúdo</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
                                    <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#c1ff72'}}>
                                        <span className="text-lg sm:text-[20px]" style={{color: '#0b0b36'}}>∞</span>
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-base sm:text-lg">Criação Ilimitada</h4>
                                        <p className="text-gray-300 text-xs sm:text-sm">Crie seus próprios flashcards personalizados</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-center order-1 lg:order-2">
                            <div className="relative w-full max-w-md sm:max-w-lg lg:max-w-none">
                                <img 
                                    src="https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1755872233695105346_68a87be9.webp" 
                                    alt="Banco de questões TeraQuiz" 
                                    className="w-full h-auto rounded-2xl shadow-xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 4,
            title: "Quem criou e temas",
            content: (
                <div>
                    {/* Idealizador Section */}
                    <div className="mb-24">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Idealizador</h3>
                            <div className="w-20 h-1 mx-auto rounded-full" style={{backgroundColor: '#c1ff72'}}></div>
                        </div>
                        
                        <div className="max-w-6xl mx-auto">
                            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                                <div className="flex justify-center order-1 lg:order-1">
                                    <div className="relative group">
                                        <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-all duration-700" style={{backgroundColor: '#c1ff72'}}></div>
                                        <div className="relative">
                                            <div className="w-80 sm:w-96 lg:w-96 h-96 sm:h-[480px] lg:h-[480px] rounded-3xl border-4 border-white/20 overflow-hidden shadow-2xl" style={{backgroundColor: '#38b6ff'}}>
                                                <img src='https://teraquiz.s3.us-east-005.backblazeb2.com/teraquiz/image_1755877506309784466_68a89082.webp' className='w-full h-full object-cover' alt="Dr. Otávio Moreira"/>
                                            </div>
                                            <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 w-12 sm:w-16 h-12 sm:h-16 rounded-full flex items-center justify-center shadow-2xl border-2 sm:border-4 border-white/20" style={{backgroundColor: '#c1ff72'}}>
                                                <span className="text-2xl sm:text-3xl">⭐</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6 sm:space-y-8 order-2 lg:order-2">
                                    <div className="group relative">
                                        <div className="absolute -inset-2 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-all duration-500" style={{backgroundColor: '#c1ff72'}}></div>
                                        <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 hover:border-white/40 transition-all duration-300">
                                            <div className="mb-6 sm:mb-8">
                                                <h4 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Dr. Otávio Moreira</h4>
                                                <div className="space-y-3 sm:space-y-4">
                                                    <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                                                        <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{backgroundColor: '#38b6ff'}}></div>
                                                        <span className="text-gray-300 text-sm sm:text-base lg:text-lg">Oncologista clínico formado pelo ICESP-FMUSP</span>
                                                    </div>
                                                    <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                                                        <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{backgroundColor: '#c1ff72'}}></div>
                                                        <span className="text-gray-300 text-sm sm:text-base lg:text-lg">Titulado pela SBOC</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Temas Abordados Section */}
                    <div>
                        <div className="text-center mb-12">
                            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Temas Abordados</h3>
                            <div className="w-20 h-1 mx-auto rounded-full" style={{backgroundColor: '#38b6ff'}}></div>
                            <p className="text-gray-300 text-lg mt-6 max-w-2xl mx-auto">Temas abordados nas questões:</p>
                        </div>
                        
                        <div className="max-w-6xl mx-auto">
                            <div className="group relative">
                                <div className="absolute -inset-2 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-all duration-500" style={{backgroundColor: '#38b6ff'}}></div>
                                <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                                        {[
                                            'Mama', 'Pulmão', 'Gastrointestinal', 'Geniturinário',
                                            'Sarcoma', 'Melanoma', 'Gineco-oncologia', 'Cabeça e pescoço',
                                            'Sistema nervoso central', 'Cuidados paliativos', 'ANS/SUS', 'Eventos imunorrelacionados',
                                            'Oncogeriatria', 'Pesquisa clínica'
                                        ].map((tema, index) => (
                                            <div key={index} className="group/item relative">
                                                <div className="absolute -inset-1 rounded-xl blur opacity-0 group-hover/item:opacity-20 transition-all duration-300" style={{backgroundColor: '#c1ff72'}}></div>
                                                <div className="relative flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 border border-white/10 hover:border-white/20">
                                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{backgroundColor: '#c1ff72'}}></div>
                                                    <span className="text-gray-300 group-hover/item:text-white transition-colors text-xs sm:text-sm font-medium leading-tight">{tema}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <div className="mt-8 pt-6 border-t border-white/10">
                                        <div className="flex items-center justify-center">
                                            <div className="flex items-center space-x-4 px-6 py-3 rounded-2xl border border-white/20" style={{backgroundColor: 'rgba(193, 255, 114, 0.1)'}}>
                                                <span className="text-gray-300 text-sm">Total de especialidades</span>
                                                <div className="w-px h-6" style={{backgroundColor: '#c1ff72', opacity: 0.3}}></div>
                                                <span className="px-4 py-2 rounded-full text-sm font-bold" style={{backgroundColor: '#c1ff72', color: '#0b0b36'}}>14 áreas</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 5,
            title: "Preço e acesso",
            content: (
                <div>
                    {/* CTA Final Section */}
                    <div className="text-center relative z-10 mb-16">
                        <div className="mb-6">
                            <div className="inline-flex items-center px-6 py-2 rounded-full font-bold text-xs shadow-lg" style={{backgroundColor: '#c1ff72', color: '#0b0b36'}}>
                                🔥 APROVEITE O LANÇAMENTO COM CONDIÇÕES ESPECIAIS!
                            </div>
                        </div>

                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                            BANCO DE QUESTÕES
                            <span className="block" style={{color: '#c1ff72'}}>ONCO</span>
                        </h2>
                        
                        <div className="grid md:grid-cols-3 gap-3 mb-8 max-w-3xl mx-auto">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                                <div className="text-[20px] font-bold mb-1" style={{color: '#c1ff72'}}>600</div>
                                <div className="text-gray-300 text-[16px]">Questões Comentadas</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                                <div className="text-[20px] font-bold mb-1" style={{color: '#c1ff72'}}>600</div>
                                <div className="text-gray-300 text-[16px]">Flashcards</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                                <div className="text-[20px] font-bold mb-1" style={{color: '#c1ff72'}}>∞</div>
                                <div className="text-gray-300 text-[16px]">Criação Ilimitada</div>
                            </div>
                        </div>

                        <div className="relative mb-8">
                            <div className="absolute -inset-1 rounded-2xl blur opacity-50" style={{backgroundColor: '#38b6ff'}}></div>
                            <div className="relative bg-white rounded-2xl p-6 shadow-xl">
                                <div className="text-center">
                                    <div className="text-red-500 text-sm font-semibold line-through mb-2 opacity-75">
                                        De R$ 3.170,00
                                    </div>
                                    
                                    <div className="mb-4">
                                        <div className="text-4xl md:text-5xl font-black leading-none" style={{color: '#0b0b36'}}>
                                            R$ 1.470<span className="text-2xl">,90</span>
                                        </div>
                                    </div>

                                    <div className="inline-flex items-center px-4 py-2 rounded-full font-semibold text-[14px] shadow-md" style={{backgroundColor: '#c1ff72', color: '#0b0b36'}}>
                                        💰 Você economiza R$ 1.700,00
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative mb-6">
                            <div className="absolute -inset-1 rounded-xl blur opacity-75" style={{backgroundColor: '#c1ff72'}}></div>
                            <button onClick={() => {
                                window.open('https://teraquiz.com/asaas?id=questoes-flashcards-oncologia-2025', '_blank');
                            }} className="relative w-full py-4 rounded-xl font-bold text-lg shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3 group" style={{backgroundColor: '#c1ff72', color: '#0b0b36'}}>
                                <span>QUERO ESTUDAR DE FORMA EFICIENTE</span>
                                <svg className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex flex-wrap justify-center items-center gap-4 text-gray-300">
                            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1">
                                <svg className="w-4 h-4" style={{color: '#c1ff72'}} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-medium">Compra Segura</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1">
                                <svg className="w-4 h-4" style={{color: '#38b6ff'}} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs font-medium">Acesso Imediato</span>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div>
                        <h2 className="text-2xl font-bold text-center text-white mb-8">
                            DÚVIDAS E RESPOSTAS
                        </h2>
                        <div className="space-y-4">
                            {faqData.map((faq, index) => (
                                <div key={index} className="border border-white/20 rounded-lg">
                                    <div className="px-6 py-4" style={{backgroundColor: 'rgba(255,255,255,0.05)'}}>
                                        <div className="font-medium text-white text-sm mb-2">{faq.question}</div>
                                        <p className="text-gray-300 text-sm">{faq.answer}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="text-center mt-12">
                            <p className="text-white mb-6">
                                Ainda tem dúvidas sobre o banco de questões? Entre em contato conosco:
                            </p>
                            <p className="text-white font-medium mb-8">
                                Para dúvidas sobre o produto e nos poucos tempo:
                            </p>
                            <button
                                onClick={() => window.open('https://wa.me/5531996102844', '_blank')}
                                className="bg-gradient-to-r from-green-400 to-green-500 text-black px-8 py-3 rounded-full font-bold hover:shadow-xl transition-all duration-300"
                            >
                                ENTRAR EM CONTATO DE FORMA RÁPIDA E EFETIVA
                            </button>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="py-12 sm:py-16 md:py-20" style={{backgroundColor: '#0b0b36'}}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                

                {/* Step Content */}
                <div className="min-h-[600px] transition-all duration-500 ease-in-out">
                    {steps.find(step => step.id === currentStep)?.content}
                </div>

                {/* Navigation Buttons */}
                <div className={`flex mt-12 ${currentStep === 1 ? 'justify-center' : 'flex-col sm:flex-row justify-between gap-4 sm:gap-0'}`}>
                    {currentStep > 1 && (
                        <button
                            onClick={() => {
                                setCurrentStep(Math.max(1, currentStep - 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-sm sm:text-base bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
                        >
                            ← Anterior
                        </button>
                    )}
                    
                    <button
                        onClick={() => {
                            if (currentStep === 5) {
                                 window.open('https://teraquiz.com/asaas?id=questoes-flashcards-oncologia-2025', '_blank');
                            } else {
                                setCurrentStep(Math.min(5, currentStep + 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                        className="w-full sm:w-auto px-8 sm:px-16 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg text-black transition-all duration-300 hover:scale-105 shadow-xl"
                        style={{backgroundColor: '#c1ff72'}}
                    >
                        {currentStep === 5 ? 'Comprar Agora' : 'Próximo →'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InteractiveQuiz;